import { Anchor, Badge, Code, Flex, Grid, Table, Text } from "@mantine/core";
import { useShow } from "@refinedev/core";
import { Show } from "@refinedev/mantine";
import { Link } from "react-router-dom";
import { DetailsCard } from "components/common/show/DetailsPair";
import { CancelPerson } from "entities/cancel_flow";
import { colorsByBrand } from "entities/cancel_flow/brand";
import { colorsByOutcome } from "entities/cancel_flow/outcome";
import { colorsByOfferType } from "entities/cancel_flow/offerType";

export const CancelPeopleShow = () => {
  const { queryResult } = useShow<CancelPerson>({
    resource: "cancel_people",
  });
  const { data, isLoading } = queryResult;
  const person = data?.data;

  if (!person) return <div />;

  return (
    <Show isLoading={isLoading} canEdit={false}>
      <Grid>
        <Grid.Col span={8}>
          <Flex gap="sm" direction="column">
            <DetailsCard title="User">
              {person.user ? (
                <Flex direction="column" gap="xs">
                  <Anchor
                    component={Link}
                    to={`/users/show/${person.user.id}`}
                    size="sm"
                    color="cyan"
                  >
                    {person.user.email}
                  </Anchor>
                  <Text size="sm">
                    {person.user.first_name} {person.user.last_name}
                  </Text>
                </Flex>
              ) : (
                <Text color="dimmed">No user linked</Text>
              )}
            </DetailsCard>

            <DetailsCard title="Survey Response">
              {person.cancel_survey_response ? (
                <Flex direction="column" gap="xs">
                  <Text size="sm">
                    <Text weight="bold" component="span">
                      Reason:{" "}
                    </Text>
                    {person.cancel_survey_response.cancellation_reason?.label ||
                      "—"}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">
                      Competitor:{" "}
                    </Text>
                    {person.cancel_survey_response.competitor || "—"}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">
                      Alternative Solution:{" "}
                    </Text>
                    {person.cancel_survey_response.alternative_solution || "—"}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">
                      Return Likelihood:{" "}
                    </Text>
                    {person.cancel_survey_response.return_likelihood ?? "—"}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">
                      Free Text:{" "}
                    </Text>
                    {person.cancel_survey_response.free_text_comment || "—"}
                  </Text>
                  {person.cancel_survey_response.custom_reason_text && (
                    <Text size="sm">
                      <Text weight="bold" component="span">
                        Custom Reason Text:{" "}
                      </Text>
                      {person.cancel_survey_response.custom_reason_text}
                    </Text>
                  )}
                </Flex>
              ) : (
                <Text color="dimmed">No survey response</Text>
              )}
            </DetailsCard>

            <DetailsCard title="Offer Interactions">
              {person.offer_interactions &&
              person.offer_interactions.length > 0 ? (
                <Table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Value</th>
                      <th>Coupon</th>
                      <th>Accepted</th>
                      <th>Shown At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {person.offer_interactions.map((interaction) => (
                      <tr key={interaction.id}>
                        <td>
                          <Badge
                            color={
                              colorsByOfferType[interaction.offer_type]
                            }
                          >
                            {interaction.offer_type}
                          </Badge>
                        </td>
                        <td>{interaction.offer_value}</td>
                        <td>{interaction.chargebee_coupon_id || "—"}</td>
                        <td>
                          <Badge
                            color={interaction.accepted ? "green" : "gray"}
                          >
                            {interaction.accepted ? "Yes" : "No"}
                          </Badge>
                        </td>
                        <td>
                          {interaction.shown_at
                            ? new Date(interaction.shown_at).toLocaleString()
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Text color="dimmed">No offer interactions</Text>
              )}
            </DetailsCard>

            <DetailsCard title="Watchlist Entry">
              {person.watchlist_entry ? (
                <Flex direction="column" gap="xs">
                  <Text size="sm">
                    <Text weight="bold" component="span">
                      Status:{" "}
                    </Text>
                    <Badge>{person.watchlist_entry.status}</Badge>
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">
                      Customer ID:{" "}
                    </Text>
                    {person.watchlist_entry.chargebee_customer_id}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">
                      Subscription ID:{" "}
                    </Text>
                    {person.watchlist_entry.chargebee_subscription_id}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">
                      MRR:{" "}
                    </Text>
                    ${person.watchlist_entry.subscription_mrr}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">
                      Deflected At:{" "}
                    </Text>
                    {person.watchlist_entry.deflected_at
                      ? new Date(
                          person.watchlist_entry.deflected_at
                        ).toLocaleString()
                      : "—"}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">
                      Resolved At:{" "}
                    </Text>
                    {person.watchlist_entry.resolved_at
                      ? new Date(
                          person.watchlist_entry.resolved_at
                        ).toLocaleString()
                      : "—"}
                  </Text>
                </Flex>
              ) : (
                <Text color="dimmed">No watchlist entry</Text>
              )}
            </DetailsCard>
          </Flex>
        </Grid.Col>

        <Grid.Col span={4}>
          <Flex direction="column" gap="sm">
            <DetailsCard title="ID">
              <Text>{person.id}</Text>
            </DetailsCard>
            <DetailsCard title="UUID">
              <Code>{person.uuid}</Code>
            </DetailsCard>
            <DetailsCard title="Brand">
              <Badge variant="filled" color={colorsByBrand[person.brand]}>
                {person.brand}
              </Badge>
            </DetailsCard>
            <DetailsCard title="Segment">
              <Text>{person.plan_segment}</Text>
            </DetailsCard>
            <DetailsCard title="Outcome">
              {person.outcome ? (
                <Badge
                  variant="filled"
                  color={colorsByOutcome[person.outcome]}
                >
                  {person.outcome}
                </Badge>
              ) : (
                <Badge color="gray">Pending</Badge>
              )}
            </DetailsCard>
            <DetailsCard title="MRR">
              <Text>
                {person.subscription_mrr
                  ? `$${person.subscription_mrr}`
                  : "—"}
              </Text>
            </DetailsCard>
            <DetailsCard title="Plan ID">
              <Code>{person.chargebee_plan_id}</Code>
            </DetailsCard>
            <DetailsCard title="Country">
              <Text>{person.customer_country || "—"}</Text>
            </DetailsCard>
            <DetailsCard title="State">
              <Text>{person.customer_state || "—"}</Text>
            </DetailsCard>
            <DetailsCard title="Chargebee Customer ID">
              <Code>{person.chargebee_customer_id}</Code>
            </DetailsCard>
            <DetailsCard title="Chargebee Subscription ID">
              <Code>{person.chargebee_subscription_id}</Code>
            </DetailsCard>
            <DetailsCard title="Started At">
              <Code>
                {person.started_at
                  ? new Date(person.started_at).toLocaleString()
                  : "—"}
              </Code>
            </DetailsCard>
            <DetailsCard title="Completed At">
              <Code>
                {person.completed_at
                  ? new Date(person.completed_at).toLocaleString()
                  : "—"}
              </Code>
            </DetailsCard>
          </Flex>
        </Grid.Col>
      </Grid>
    </Show>
  );
};
